<template>
  <div class="pa-6">
    <div class="clients-range-slider py-5">
      <mf-loading loader-type="dynamic" :dynamic-amount="2" :boolean-loader="sliderLoading">
        <p class="client-label pl-6 mb-1 d-flex align-center">
          <mf-icon size="20" class="mr-2"> info </mf-icon>{{ localeString(clients.total_count, 0) }} {{ title }}
        </p>
        <div class="clients-chart px-7">
          <div
            v-for="(item, index) in filteredLegendContent"
            :key="item.color"
            :class="{ 'first-chart': index === 0, 'last-chart': index === filteredLegendContent.length - 1 }"
            :style="`width: ${item.percentage}%; height: 12px; background: ${item.color}`"
          />
        </div>
        <div class="legend-content px-7 d-flex align-center">
          <div v-for="item in legendContent" :key="item.color" class="d-flex align-center">
            <div class="item-circle mr-1" :style="`background-color: ${item.color}`" />
            <span>{{ item.percentage }}% - {{ item.label }}</span>
          </div>
        </div>
      </mf-loading>
    </div>
  </div>
</template>

<script>
import { localeString } from '@/config/helpers.js'
import { QUERY_GET_AGGREGATED_CLIENTS_STATUS_INFO } from '@/modules/maria/campanha-da-maria/graphql'

export default {
  name: 'ClientsRangeSlider',
  props: {
    channel: {
      type: String,
      required: true,
    },
    campaignVersion: {
      type: String,
      default: () => 'V1',
    },
    segments: {
      type: Array,
      default: () => [],
    },
    version: {
      type: Number,
      required: true,
    },
  },
  data: () => ({
    sliderLoading: false,
    clients: {},
    clientsDistribution: {
      client_active_count: 0,
      client_inactive_count: 0,
      client_lost_count: 0,
      client_never_bought_count: 0,
      client_non_loyalty_count: 0,
      client_at_risk_count: 0,
      client_losses_count: 0,
      client_recent_inactive_count: 0,
    },
  }),
  computed: {
    title() {
      switch (this.channel) {
        case 'whatsapp':
          return `clientes cadastrados no whatsapp`
        case 'push':
          return `clientes possuem o aplicativo instalado`
        case 'sms':
          return `clientes com um número de telefone válido`
        case 'email':
          return `clientes com um endereço de e-mail válido`
        default:
          return ''
      }
    },
    isWhatsappChannel() {
      return this.channel === 'whatsapp'
    },
    nonLoyalty() {
      const nonLoyaltyCount = localeString(this.clients.client_non_loyalty_count, 0)
      if (!nonLoyaltyCount) return 0 + ' contatos fora do clube'
      return nonLoyaltyCount === 1 ? '1 contato fora do clube' : `${nonLoyaltyCount} contatos fora do clube`
    },
    legendContent() {
      if (this.version === 2) {
        return [
          {
            color: '#0FC94D',
            percentage: this.clientsDistribution.client_active_count || 0,
            value: this.clients.client_active_count,
            label: this.getLabel(this.clients.client_active_count, 'ativo', 'ativos'),
          },
          {
            color: '#F3AF00',
            percentage: this.clientsDistribution.client_recent_inactive_count || 0,
            value: this.clients.client_recent_inactive_count,
            label: this.getLabel(this.clients.client_recent_inactive_count, 'inativo recente', 'inativos recentes'),
          },
          {
            color: '#F36600',
            percentage: this.clientsDistribution.client_at_risk_count || 0,
            value: this.clients.client_at_risk_count,
            label: this.getLabel(this.clients.client_at_risk_count, 'risco de perder', 'risco de perder'),
          },
          {
            color: '#B3280D',
            percentage: this.clientsDistribution.client_losses_count || 0,
            value: this.clients.client_losses_count,
            label: this.getLabel(this.clients.client_losses_count, 'perdido', 'perdidos'),
          },
          {
            color: '#969EA7',
            percentage: this.clientsDistribution.client_never_bought_count || 0,
            value: this.clients.client_never_bought_count,
            label: this.getLabel(this.clients.client_never_bought_count, 'nunca comprou', 'nunca compraram'),
          },
          ...(this.isWhatsappChannel
            ? [
                {
                  color: '#BF3600',
                  percentage: this.clientsDistribution.client_non_loyalty_count || 0,
                  value: this.clients.client_non_loyalty_count,
                  label: this.nonLoyalty,
                },
              ]
            : []),
        ]
      } else {
        return [
          {
            color: '#002040',
            percentage: this.clientsDistribution.client_active_count || 0,
            value: this.clients.client_active_count,
            label: this.getLabel(this.clients.client_active_count, 'ativo', 'ativos'),
          },
          {
            color: '#00B395',
            percentage: this.clientsDistribution.client_inactive_count || 0,
            value: this.clients.client_inactive_count,
            label: this.getLabel(this.clients.client_inactive_count, 'inativo', 'inativos'),
          },
          {
            color: '#0079F2',
            percentage: this.clientsDistribution.client_lost_count || 0,
            value: this.clients.client_lost_count,
            label: this.getLabel(this.clients.client_lost_count, 'perdido', 'perdidos'),
          },
          {
            color: '#969EA7',
            percentage: this.clientsDistribution.client_never_bought_count || 0,
            value: this.clients.client_never_bought_count,
            label: this.getLabel(this.clients.client_never_bought_count, 'nunca comprou', 'nunca compraram'),
          },
          ...(this.isWhatsappChannel
            ? [
                {
                  color: '#BF3600',
                  percentage: this.clientsDistribution.client_non_loyalty_count || 0,
                  value: this.clients.client_non_loyalty_count,
                  label: this.nonLoyalty,
                },
              ]
            : []),
        ]
      }
    },
    filteredLegendContent() {
      return this.legendContent.filter((item) => item.value > 0)
    },
  },
  async mounted() {
    await this.getData()
  },
  methods: {
    getLabel(value = 0, singularLabel, pluralLabel) {
      const count = localeString(value, 0)
      return count === 1 ? `1 cliente ${singularLabel}` : `${count} clientes ${pluralLabel}`
    },
    async getData() {
      const segments_ids = this.segments.map((segment) => segment._id)
      try {
        this.sliderLoading = true
        await this.$apollo
          .query({
            query: QUERY_GET_AGGREGATED_CLIENTS_STATUS_INFO,
            fetchPolicy: 'no-cache',
            variables: {
              segments_ids,
              version: this.version === 1 ? 'V1' : 'V2',
            },
          })
          .then((response) => {
            this.clients = response.data.getAggregatedClientStatusInfo[this.channel]
            if (this.version === 2) {
              this.clientsDistribution = {
                client_active_count: parseFloat(((this.clients?.client_active_count / this.clients?.total_count) * 100).toPrecision(3)),
                client_at_risk_count: parseFloat(((this.clients?.client_at_risk_count / this.clients?.total_count) * 100).toPrecision(3)),
                client_losses_count: parseFloat(((this.clients?.client_losses_count / this.clients?.total_count) * 100).toPrecision(3)),
                client_recent_inactive_count: parseFloat(((this.clients?.client_recent_inactive_count / this.clients?.total_count) * 100).toPrecision(3)),
                client_never_bought_count: parseFloat(((this.clients?.client_never_bought_count / this.clients?.total_count) * 100).toPrecision(3)),
                client_non_loyalty_count: parseFloat(((this.clients?.client_non_loyalty_count / this.clients?.total_count) * 100).toPrecision(3)),
              }
            } else {
              this.clientsDistribution = {
                client_active_count: parseFloat(((this.clients?.client_active_count / this.clients?.total_count) * 100).toPrecision(3)),
                client_inactive_count: parseFloat(((this.clients?.client_inactive_count / this.clients?.total_count) * 100).toPrecision(3)),
                client_lost_count: parseFloat(((this.clients?.client_lost_count / this.clients?.total_count) * 100).toPrecision(3)),
                client_never_bought_count: parseFloat(((this.clients?.client_never_bought_count / this.clients?.total_count) * 100).toPrecision(3)),
                client_non_loyalty_count: parseFloat(((this.clients?.client_non_loyalty_count / this.clients?.total_count) * 100).toPrecision(3)),
              }
            }
          }, [])
      } catch (error) {
        this.$snackbar({ message: 'Erro ao buscar dados no servidor', snackbarColor: 'error' })
      } finally {
        this.sliderLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
.clients-range-slider {
  background: $cinza-l90;
  border-radius: 8px;

  .client-label,
  .legend-content > span {
    color: $cinza-d10;
    font-size: 14px;
  }

  .legend-content {
    gap: 24px;
    flex-wrap: wrap;
    .item-circle {
      width: 9px;
      height: 9px;
      border-radius: 100%;
    }

    span {
      color: $cinza-d10;
      font-size: 14px;
    }
  }

  .clients-chart {
    height: 12px;
    width: 100%;
    display: inline-flex;

    .first-chart {
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }

    .last-chart {
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
    }
  }
}
</style>
